let audioCtx: AudioContext | null = null;
let fallbackAudio: HTMLAudioElement | null = null;
let fallbackAudioPromise: Promise<HTMLAudioElement | null> | null = null;

function getAudioContext(): AudioContext | null {
  const AudioCtx =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return null;
  if (!audioCtx) audioCtx = new AudioCtx();
  return audioCtx;
}

function primeAudioContext(ctx: AudioContext): void {
  const buffer = ctx.createBuffer(1, 1, 22050);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start(0);
}

/** Call after user interaction so later message sounds are allowed by the browser. */
export function unlockMessageSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  void ctx.resume().then(() => {
    if (ctx.state !== 'running') return;
    try {
      primeAudioContext(ctx);
    } catch {
      /* ignore */
    }
  });
}

export function initMessageSoundUnlock(): () => void {
  const unlock = () => unlockMessageSound();
  document.addEventListener('pointerdown', unlock, { passive: true });
  document.addEventListener('keydown', unlock, { passive: true });
  document.addEventListener('focus', unlock, { passive: true });
  return () => {
    document.removeEventListener('pointerdown', unlock);
    document.removeEventListener('keydown', unlock);
    document.removeEventListener('focus', unlock);
  };
}

function playWithWebAudio(): boolean {
  const ctx = getAudioContext();
  if (!ctx) return false;

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(660, ctx.currentTime);
  oscillator.frequency.setValueAtTime(880, ctx.currentTime + 0.08);
  oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.16);
  gainNode.gain.setValueAtTime(0.35, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
  oscillator.start();
  oscillator.stop(ctx.currentTime + 0.35);
  return true;
}

function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const sample = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }

  return buffer;
}

async function createFallbackAudio(): Promise<HTMLAudioElement | null> {
  if (fallbackAudio) return fallbackAudio;
  if (!fallbackAudioPromise) {
    fallbackAudioPromise = (async () => {
      const OfflineCtx =
        window.OfflineAudioContext ||
        (window as typeof window & { webkitOfflineAudioContext?: typeof OfflineAudioContext })
          .webkitOfflineAudioContext;
      if (!OfflineCtx) return null;

      const sampleRate = 44100;
      const duration = 0.35;
      const offline = new OfflineCtx(1, sampleRate * duration, sampleRate);
      const oscillator = offline.createOscillator();
      const gainNode = offline.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(offline.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(660, 0);
      oscillator.frequency.setValueAtTime(880, 0.08);
      oscillator.frequency.setValueAtTime(660, 0.16);
      gainNode.gain.setValueAtTime(0.35, 0);
      gainNode.gain.exponentialRampToValueAtTime(0.001, duration - 0.02);
      oscillator.start(0);
      oscillator.stop(duration);
      const rendered = await offline.startRendering();
      const wav = encodeWav(rendered.getChannelData(0), sampleRate);
      const url = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
      fallbackAudio = new Audio(url);
      fallbackAudio.volume = 0.5;
      return fallbackAudio;
    })();
  }
  return fallbackAudioPromise;
}

async function playFallbackBeep(): Promise<void> {
  const audio = await createFallbackAudio();
  if (!audio) return;
  audio.currentTime = 0;
  await audio.play().catch(() => undefined);
}

/** Short notification tone for incoming chat messages. */
export async function playMessageSound(): Promise<void> {
  unlockMessageSound();
  try {
    const ctx = getAudioContext();
    if (ctx) {
      if (ctx.state !== 'running') {
        await ctx.resume();
      }
      if (ctx.state === 'running') {
        playWithWebAudio();
        return;
      }
    }
    await playFallbackBeep();
  } catch {
    await playFallbackBeep();
  }
}
